/**
 * FutureWorthTab - a "what if" net worth calculator.
 *
 * Deliberately NOT connected to the user's own Worthly data: every field
 * starts blank so it can be used for any plan, including someone else's.
 * Nothing here changes a real balance or projection anywhere in the app.
 *
 * Storage:
 *   - Snapshots live on userData.future_worth.snapshots and are saved with
 *     saveUserData({ future_worth }). A snapshot stores its INPUTS, never its
 *     results, so a change to the maths recalculates every saved plan.
 *   - The calculator draft is kept in localStorage only, so leaving the page
 *     does not wipe what someone was midway through typing.
 *
 * Figures are the plain amount someone would have - no inflation adjustment.
 * That was tried and removed: "today's purchasing power" needed more
 * explaining than it was worth on a planning tool.
 *
 * Every component below is declared at module scope on purpose: a component
 * defined inside another one is a new type on every render, which remounts it
 * and drops keyboard focus mid-typing.
 */
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, Minus, ChevronLeft } from "lucide-react";
import { formatCurrencyWhole } from "../../utils/formatters";
import {
  Sheet,
  SHEET_FIELD_LABEL,
  SHEET_INPUT_CLS,
  SHEET_INPUT_STYLE,
  PLAN_TILE_PALETTES,
} from "../plans/plansShared";
import { usePurple } from "../../utils/purple";
import { usePlan, PLAN_TILES } from "../../utils/plan"; // plan_04
import PlanPath from "../common/PlanPath"; // plan_15

const DRAFT_KEY = 'worthly_future_worth_draft';
const NOTE_MAX = 200;
const MAX_COMPARE = 3;
const YEAR_PRESETS = [5, 10, 20, 30];

// Fixed across themes, like the planner's "short" status: amber reads as
// "not yet", which is what a debt-free date is.
const AMBER_TILE = { bg: 'linear-gradient(135deg, #FDF0E2 0%, #F8E2C4 100%)', label: '#8A5A1E', value: '#8A5A1E' };
const WARN = { bg: '#FDECEF', text: '#B4153A' };
const POSITIVE = '#2F6B44';
const NEGATIVE = '#B4153A';

const newId = (prefix) => prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

// The amount field keeps its original key ('monthly' / 'payment') so saved
// snapshots stay readable; `freq` says how often that amount happens.
const FREQUENCIES = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'biweekly', label: 'Every 2 weeks' },
  { key: 'quarterly', label: 'Quarterly' },
];

const blankAccount = (prefix) => ({ id: newId(prefix), name: '', start: '', monthly: '', freq: 'monthly', rate: '' });
const blankDebt = () => ({ id: newId('db'), name: '', balance: '', rate: '', payment: '', freq: 'monthly' });
// plan_17: an asset keeps the same value every year (a car, a home).
const blankAsset = () => ({ id: newId('as'), name: '', value: '', dir: 'same', change: '' });
// plan_18: which way an asset's value moves each year. There's no minus key
// on the iPhone number pad, so the direction is picked rather than typed.
const ASSET_DIRS = [
  { key: 'same', label: 'Same value' },
  { key: 'up', label: 'Goes up' },
  { key: 'down', label: 'Goes down' },
];
const assetYearlyRate = (a) => {
  const pct = Math.abs(num(a.change)) / 100;
  return a.dir === 'up' ? pct : a.dir === 'down' ? -pct : 0;
};
const blankInputs = () => ({
  savings: [blankAccount('sv')],
  investments: [blankAccount('iv')],
  assets: [blankAsset()],
  debts: [blankDebt()],
});

const num = (v) => {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Whole-currency figures: cents on a ten-year guess suggest precision it does not have. */
const money = (v) => {
  const r = Math.round(v || 0);
  return (r < 0 ? '-' : '') + formatCurrencyWhole(Math.abs(r));
};

const duration = (months) => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  const out = [];
  if (y) out.push(y + (y === 1 ? ' yr' : ' yrs'));
  if (m) out.push(m + ' mo');
  return out.join(' ') || 'Now';
};

/** Old or partial records never crash the page. */
const normalizeInputs = (inp) => {
  const i = inp || {};
  return {
    savings: Array.isArray(i.savings) ? i.savings : [],
    investments: Array.isArray(i.investments) ? i.investments : [],
    assets: Array.isArray(i.assets) ? i.assets : [],
    debts: Array.isArray(i.debts) ? i.debts : [],
  };
};

const hasAnyData = (inp) =>
  inp.savings.concat(inp.investments).some((a) => num(a.start) || num(a.monthly)) ||
  inp.assets.some((a) => num(a.value)) ||
  inp.debts.some((d) => num(d.balance));

/** How much of an amount lands in month m (1-based), given how often it happens. */
const amountInMonth = (amount, freq, m) => {
  if (freq === 'biweekly') return (amount * 26) / 12;
  if (freq === 'quarterly') return m % 3 === 0 ? amount : 0;
  return amount;
};

/** The same amount expressed per month, for summaries and the interest check. */
const monthlyEquivalent = (amount, freq) => {
  if (freq === 'biweekly') return (amount * 26) / 12;
  if (freq === 'quarterly') return amount / 3;
  return amount;
};

/**
 * Monthly compounding at a constant rate. Every-2-weeks amounts are spread as
 * 26 a year; quarterly amounts land every third month. Debts accrue interest
 * and are paid down; a debt that is paid off does not redirect its payment.
 */
export function projectFutureWorth(inputs, years) {
  const inp = normalizeInputs(inputs);
  const grow = inp.savings.concat(inp.investments).map((a) => ({
    b: num(a.start), amt: num(a.monthly), freq: a.freq || 'monthly', r: num(a.rate) / 1200,
  }));
  const debts = inp.debts.map((d) => {
    const bal = num(d.balance);
    const r = num(d.rate) / 1200;
    const pay = num(d.payment);
    const freq = d.freq || 'monthly';
    return {
      id: d.id, b: bal, r, pay, freq, free: bal > 0 ? null : 0,
      grows: bal > 0 && monthlyEquivalent(pay, freq) <= bal * r,
    };
  });

  let put = grow.reduce((t, a) => t + a.b, 0);
  // Assets hold their value: counted in net worth every year, but never as
  // money put in or as growth.
  // plan_18: each asset can go up or down once a year.
  const heldItems = inp.assets.map((a) => ({ v: num(a.value), r: assetYearlyRate(a) }));
  const heldStart = heldItems.reduce((t, a) => t + a.v, 0);
  const held = () => heldItems.reduce((t, a) => t + a.v, 0);
  const assets = () => grow.reduce((t, a) => t + a.b, 0);
  const owed = () => debts.reduce((t, d) => t + d.b, 0);

  const net = [];
  const putLine = [];
  const push = () => { net.push(assets() + held() - owed()); putLine.push(put); };
  push();

  for (let m = 1; m <= years * 12; m += 1) {
    grow.forEach((a) => {
      const add = amountInMonth(a.amt, a.freq, m);
      a.b = a.b * (1 + a.r) + add;
      put += add;
    });
    debts.forEach((d) => {
      if (d.b > 0) {
        d.b = d.b * (1 + d.r) - amountInMonth(d.pay, d.freq, m);
        if (d.b <= 0.005) { d.b = 0; if (d.free === null) d.free = m; }
      }
    });
    if (m % 12 === 0) {
      heldItems.forEach((a) => { a.v = Math.max(0, a.v * (1 + a.r)); });
      push();
    }
  }

  const startingDebt = inp.debts.some((d) => num(d.balance) > 0);
  let debtFreeMonth = 0;
  if (startingDebt) {
    debtFreeMonth = debts.some((d) => d.free === null) ? null : Math.max(...debts.map((d) => d.free));
  }

  return {
    net,
    put: putLine,
    end: assets() + held() - owed(),
    assetsHeld: heldStart,
    assetsEnd: held(),
    putIn: put,
    growth: assets() - put,
    debtLeft: owed(),
    hasDebt: startingDebt,
    debtFreeMonth,
    growingDebtIds: debts.filter((d) => d.grows).map((d) => d.id),
  };
}


// plan_15: Money plan look for Future Worth - one hero card with a path to
// your chosen year, soft tiles, and calmer forms. The maths is unchanged.
const PLAN_FW = {
  ink: '#2B2C31', soft: '#7A7A82', line: '#EEEAF6', field: '#F7F5FB',
  net: '#A58BE6', put: '#7CC79C',
};
const shortMoney = (v) => {
  const a = Math.abs(v || 0);
  const s = a >= 1e6 ? (a / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M'
    : a >= 1e3 ? Math.round(a / 1e3) + 'k' : String(Math.round(a));
  return (v < 0 ? '-' : '') + formatCurrencyWhole(0).replace(/0([.,]00)?/, s);
};
const PlanFwHero = ({ p, years, hasData, debtFreeValue }) => {
  const mid = Math.round(years / 2);
  const steps = [{ emoji: '\u{1F331}', label: 'Now', amount: hasData ? shortMoney(p.net[0]) : null, done: true }];
  if (years >= 2) steps.push({ emoji: '\u{1F33F}', label: mid + (mid === 1 ? ' year' : ' years'), amount: hasData ? shortMoney(p.net[mid]) : null, now: true });
  steps.push({ emoji: '\u{1F333}', label: years + (years === 1 ? ' year' : ' years'), amount: hasData ? shortMoney(p.net[years]) : null, now: years < 2 });
  const tile = (bg, ink, k, v) => (
    <div style={{ background: bg, borderRadius: 20, padding: 14 }}>
      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: ink }}>{k}</span>
      <span style={{ display: 'block', fontSize: 19, fontWeight: 600, color: PLAN_FW.ink, marginTop: 4, letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
    </div>
  );
  return (
    <div style={{ background: '#FFFFFF', borderRadius: 28, padding: '20px 18px 18px' }} data-testid="plan-fw-hero">
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: PLAN_FW.ink }}>In {years} {years === 1 ? 'year' : 'years'} {'\u2728'}</h3>
          <p style={{ margin: '5px 0 0', fontSize: 13.5, color: PLAN_FW.soft, lineHeight: 1.45 }}>
            {!hasData ? 'Fill in any section below to see where you’d be.' : p.hasDebt ? (p.assetsHeld > 0 ? 'Your savings, investments and assets, minus any debt left.' : 'Your savings and investments, minus any debt left.') : 'Where you’d be if you keep this up.'}
          </p>
        </div>
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 50, height: 50, borderRadius: 999, background: '#ECE6FA', fontSize: 23 }}>{'\u{1F331}'}</span>
      </div>
      <p style={{ margin: '14px 0 0', fontSize: 36, fontWeight: 600, letterSpacing: '-0.04em', color: PLAN_FW.ink, lineHeight: 1.1 }}>{money(hasData ? p.end : 0)}</p>
      {hasData && p.growth > 0 && (
        <span style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5, fontWeight: 600, color: '#2F8756', background: '#DFF2E7', borderRadius: 999, padding: '4px 10px' }}>
          +{money(p.growth)} from growth
        </span>
      )}
      <PlanPath steps={steps} progress={0} />
      <div className="grid grid-cols-2" style={{ gap: 10, marginTop: 14 }}>
        {tile('#DFF2E7', '#2F8756', 'You put in', money(hasData ? p.putIn : 0))}
        {tile('#ECE6FA', '#7A5DD3', 'Growth', money(hasData ? p.growth : 0))}
        {tile('#DFE9F9', '#3C6DB5', 'Debt left', money(p.debtLeft))}
        {tile('#FAE4ED', '#C04B7C', 'Debt-free in', debtFreeValue)}
      </div>
    </div>
  );
};

const CHART_W = 318;
const CHART_H = 150;

const linePath = (values, lo, hi, steps) => {
  const span = hi - lo || 1;
  const dx = CHART_W / (steps || 1);
  return values
    .map((v, i) => (i ? 'L' : 'M') + (i * dx).toFixed(1) + ' ' + (CHART_H - ((v - lo) / span) * CHART_H).toFixed(1))
    .join(' ');
};

const Eyebrow = ({ color, right, children }) => (usePlan() ? (
  <div className="flex items-center justify-between" style={{ margin: '0 4px 10px', gap: 10 }}>
    <p className="m-0" style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: PLAN_FW.ink }}>{children}</p>
    {right ? <span style={{ fontSize: 12.5, color: '#8B879A' }}>{right}</span> : null}
  </div>
) : (
  <div className="flex items-center justify-between px-1 mb-2">
    <p className="m-0 text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color }}>
      <span className="wy-star">✦ </span>{children}
    </p>
    {right ? <span className="text-[12px] text-gray-500">{right}</span> : null}
  </div>
));

const Tile = ({ emoji, label, value, tone }) => (
  <div className="rounded-[22px] wy-tile wy-tile-ink" style={{ background: tone.bg, padding: '12px 12px 11px' }}>
    <div
      className="rounded-lg flex items-center justify-center wy-chip"
      data-wy-tint="1"
      style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '10px' }}
    >
      {emoji}
    </div>
    <span className="wy-tile-label" style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tone.label, marginBottom: '2px' }}>
      {label}
    </span>
    <p className="m-0 wy-tile-value" style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: tone.value }}>
      {value}
    </p>
  </div>
);

const AssetDirectionPicker = ({ value, onChange, accent }) => {
  const plan = usePlan();
  return (
    <div className={plan ? 'grid grid-cols-3 gap-1 p-[3px] mb-3' : 'grid grid-cols-3 gap-1 rounded-xl p-[3px] mb-3'} style={{ background: plan ? '#ECE8F4' : '#F8F1F5', borderRadius: plan ? 999 : undefined }} role="radiogroup" aria-label="Each year">
      {ASSET_DIRS.map((d) => {
        const on = (value || 'same') === d.key;
        return (
          <button
            key={d.key}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(d.key)}
            className="rounded-[10px] text-[12.5px]"
            style={{
              height: '32px',
              borderRadius: plan ? 999 : undefined,
              background: on ? '#fff' : 'transparent',
              color: on ? (plan ? '#2B2C31' : accent) : '#6B6570',
              fontWeight: on ? (plan ? 600 : 700) : 500,
              boxShadow: on && !plan ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
            }}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
};

const FrequencyPicker = ({ value, onChange, accent }) => {
  const plan = usePlan(); // plan_15
  return (
  <div className={plan ? 'grid grid-cols-3 gap-1 p-[3px] mb-3' : 'grid grid-cols-3 gap-1 rounded-xl p-[3px] mb-3'} style={{ background: plan ? '#ECE8F4' : '#F8F1F5', borderRadius: plan ? 999 : undefined }} role="radiogroup" aria-label="How often">
    {FREQUENCIES.map((f) => {
      const on = (value || 'monthly') === f.key;
      return (
        <button
          key={f.key}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onChange(f.key)}
          className="rounded-[10px] text-[12.5px]"
          style={{
            height: '32px',
            borderRadius: plan ? 999 : undefined,
            background: on ? '#fff' : 'transparent',
            color: on ? (plan ? PLAN_FW.ink : accent) : '#6B6570',
            fontWeight: on ? (plan ? 600 : 700) : 500,
            boxShadow: on && !plan ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
          }}
        >
          {f.label}
        </button>
      );
    })}
  </div>
  );
};

const NumberField = ({ id, label, value, onChange, prefix, suffix, first }) => {
  const plan = usePlan(); // plan_15
  return (
  <div
    className="flex items-center justify-between gap-3"
    style={{ minHeight: '56px', borderTop: first ? 'none' : '1px solid ' + (plan ? PLAN_FW.line : '#F3E8EE') }}
  >
    <label htmlFor={id} className="text-[15px]" style={{ color: plan ? '#55565C' : '#1F2937', fontWeight: 500, fontSize: plan ? 14.5 : undefined }}>
      {label}
    </label>
    <div
      className="flex items-center gap-1 rounded-xl px-3 flex-shrink-0"
      style={{ background: plan ? PLAN_FW.field : '#F8F1F5', height: '42px', width: '130px', borderRadius: plan ? 14 : undefined }}
    >
      {prefix ? <span className="text-[15px] text-gray-500">{prefix}</span> : null}
      <input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 bg-transparent border-none outline-none text-right text-[16px]"
        style={{ color: '#1F2937', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}
      />
      {suffix ? <span className="text-[15px] text-gray-500">{suffix}</span> : null}
    </div>
  </div>
  );
};

// [field, label, type, has a frequency picker under it]
const SECTION_FIELDS = {
  savings: [
    ['start', 'Starting amount', 'money'],
    ['monthly', 'Amount added', 'money', true],
    ['rate', 'Interest rate', 'pct'],
  ],
  investments: [
    ['start', 'Starting amount', 'money'],
    ['monthly', 'Amount added', 'money', true],
    ['rate', 'Yearly growth rate', 'pct'],
  ],
  assets: [
    ['value', 'Value today', 'money'],
    ['change', 'Change each year', 'pct'],
  ],
  debts: [
    ['balance', 'Balance owed today', 'money'],
    ['rate', 'Interest rate', 'pct'],
    ['payment', 'Payment', 'money', true],
  ],
};

const EntryCard = ({ kind, entry, fallbackName, emoji, chip, currency, accent, warn, onChange, onRemove }) => {
  const plan = usePlan(); // plan_15
  return (
  <div className={plan ? 'bg-white' : 'bg-white rounded-2xl shadow-sm'} style={plan ? { borderRadius: 28, padding: '14px 12px 8px 18px' } : { padding: '10px 10px 6px 16px' }}>
    <div className="flex items-center gap-2.5">
      <div
        className="rounded-lg flex items-center justify-center flex-shrink-0"
        style={plan ? { width: '38px', height: '38px', borderRadius: 13, background: chip, fontSize: '18px' } : { width: '30px', height: '30px', background: chip, fontSize: '14px' }}
      >
        {emoji}
      </div>
      <input
        type="text"
        aria-label="Name"
        placeholder={fallbackName}
        value={entry.name || ''}
        onChange={(e) => onChange('name', e.target.value)}
        className="flex-1 min-w-0 bg-transparent border-none outline-none text-[16px]"
        style={{ color: plan ? PLAN_FW.ink : '#1F2937', fontWeight: 600, height: '42px', fontSize: plan ? 15.5 : undefined, fontFamily: "'DM Sans', sans-serif" }}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={'Remove ' + (entry.name || fallbackName)}
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      >
        <Trash2 className="w-[17px] h-[17px]" style={{ color: plan ? '#C4BADF' : '#A39FA0' }} />
      </button>
    </div>
    <div className="pr-1.5">
      {SECTION_FIELDS[kind].map(([field, label, type, withFreq], i) => (
        <div key={field}>
          {/* plan_18: an asset's yearly change only asks for a % once it moves */}
          {field === 'change' && (
            <AssetDirectionPicker value={entry.dir} onChange={(v) => onChange('dir', v)} accent={accent} />
          )}
          {(field !== 'change' || entry.dir === 'up' || entry.dir === 'down') && (
          <NumberField
            id={entry.id + '-' + field}
            label={label}
            value={entry[field] || ''}
            onChange={(v) => onChange(field, v)}
            prefix={type === 'money' && currency.before ? currency.symbol : ''}
            suffix={type === 'pct' ? '%' : (type === 'money' && !currency.before ? currency.symbol : '')}
            first={i === 0 || field === 'change'}
          />
          )}
          {withFreq && (
            <FrequencyPicker value={entry.freq} onChange={(v) => onChange('freq', v)} accent={accent} />
          )}
        </div>
      ))}
      {warn && (
        <p className="m-0 mt-1 mb-2.5 rounded-xl text-[13px] leading-snug" style={{ background: WARN.bg, color: WARN.text, padding: '9px 12px' }}>
          These payments don't cover the interest, so the balance keeps growing.
        </p>
      )}
    </div>
  </div>
  );
};

const SnapshotSheet = ({ mode, initialName, initialNote, placeholder, years, value, hero, accent, track, onSave, onClose, source = null }) => {
  // When the calculator was opened from a saved snapshot, saving asks whether
  // to update that snapshot or keep it and save these numbers as a new one.
  const [target, setTarget] = useState(source ? 'update' : 'new');
  const [name, setName] = useState(source ? source.name || '' : initialName || '');
  const [note, setNote] = useState(source ? source.note || '' : initialNote || '');
  const updating = mode !== 'edit' && source && target === 'update';

  const chooseTarget = (next) => {
    if (next === target) return;
    setTarget(next);
    if (next === 'update') { setName(source.name || ''); setNote(source.note || ''); }
    else { setName(''); setNote(''); }
  };

  return (
    <Sheet
      title={mode === 'edit' ? 'Edit snapshot' : 'Save snapshot'}
      subtitle={mode === 'edit' ? 'Numbers stay as saved. Open it in the calculator to try changes.' : null}
      onClose={onClose}
      track={track}
      padBottom={360}
    >
      {mode !== 'edit' && source && (
        <div className="grid grid-cols-2 gap-1 rounded-2xl p-1 mb-4" style={{ background: '#F8F1F5' }} role="radiogroup" aria-label="Where to save">
          {[
            { k: 'update', label: 'Update "' + (source.name || 'snapshot') + '"' },
            { k: 'new', label: 'Save as new' },
          ].map((o) => {
            const on = target === o.k;
            return (
              <button
                key={o.k}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => chooseTarget(o.k)}
                className="rounded-xl text-[13px] px-2 truncate"
                style={{ height: '40px', background: on ? '#fff' : 'transparent', color: on ? accent : '#6B6570', fontWeight: on ? 700 : 500, boxShadow: on ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="rounded-2xl mb-4 wy-tile" style={{ background: hero.bg, padding: '12px 14px' }}>
        <p className="m-0 text-[12px]" style={{ color: hero.label }}>
          Net worth in {years} {years === 1 ? 'year' : 'years'}
        </p>
        <p className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', color: hero.value }}>
          {value}
        </p>
      </div>

      <label htmlFor="fw-snap-name" className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Name</label>
      <input
        id="fw-snap-name"
        type="text"
        value={name}
        placeholder={placeholder}
        onChange={(e) => setName(e.target.value)}
        className={SHEET_INPUT_CLS}
        style={SHEET_INPUT_STYLE}
      />
      {mode !== 'edit' && !updating && (
        <div className="flex flex-wrap gap-2 mt-2">
          {['My plan', 'Pay off debt first', 'For a friend'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setName(s)}
              className="rounded-full text-[13px] font-semibold px-3.5"
              style={{ height: '34px', border: '1.5px solid #EBDCE4', color: '#6B4F5E', background: '#fff' }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-baseline justify-between mt-4">
        <label htmlFor="fw-snap-note" className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Note <span className="font-normal text-gray-500">(optional)</span>
        </label>
        <span className="text-[12px] text-gray-500">{note.length}/{NOTE_MAX}</span>
      </div>
      <textarea
        id="fw-snap-note"
        rows={3}
        maxLength={NOTE_MAX}
        value={note}
        placeholder="e.g. Assumes I get the raise in March"
        onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
        className={SHEET_INPUT_CLS + ' resize-none'}
        style={{ ...SHEET_INPUT_STYLE, lineHeight: 1.45 }}
      />

      <div className="flex gap-2.5 mt-5">
        <button
          type="button"
          onClick={onClose}
          className="rounded-full text-[15px] font-semibold px-5"
          style={{ height: '50px', border: '1.5px solid #E6D3DD', color: '#6B4F5E' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(name.trim(), note.trim(), updating ? source.id : null)}
          className="flex-1 rounded-full text-[15px] font-bold text-white"
          style={{ height: '50px', background: accent }}
        >
          {mode === 'edit' ? 'Save changes' : updating ? 'Update snapshot' : 'Save'}
        </button>
      </div>
    </Sheet>
  );
};

const readDraft = () => {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return { inputs: normalizeInputs(d.inputs), years: Number(d.years) || 10, openedFrom: d.openedFrom || null };
  } catch (e) {
    return null;
  }
};

const FutureWorthTab = ({ futureWorth, saveUserData, theme = 'pink', onBack }) => {
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only): lavender instead of hot pink and multicolour tiles.
  const purple = usePurple();
  // plan_04: Money plan - black buttons, pastel tiles, calm fields.
  const plan = usePlan();
  const eyebrow = purple ? '#7B5AA6' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const accent = plan ? '#1F1E24' : purple ? '#8B5CE0' : isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE';
  const track = plan ? '#F5F2FA' : purple ? '#F3ECFB' : isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';
  const muted = '#a39fa0';
  const tiles = plan ? PLAN_TILES : PLAN_TILE_PALETTES[purple ? 'purple' : isPink ? 'pink' : isWarm ? 'warm' : 'blue'];
  const snapColors = plan ? ['#A58BE6', '#7CC79C', '#7FA9E4', '#E79BBB'] : [tiles[0].value, tiles[3].value, tiles[1].value, tiles[2].value];
  const netColor = plan ? PLAN_FW.net : tiles[0].value; // plan_15
  const putColor = plan ? PLAN_FW.put : tiles[3].value;
  const bigNum = plan ? { fontWeight: 600, letterSpacing: '-0.5px' } : null;
  const LINE = plan ? PLAN_FW.line : '#F3E8EE';
  const serif = { fontFamily: "'DM Serif Display', serif", fontWeight: 400 };

  // Which side of the number the symbol goes on, taken from the user's own
  // currency formatting rather than assumed to be a leading "$".
  const currency = useMemo(() => {
    const sample = formatCurrencyWhole(0);
    const symbol = sample.replace(/[\d\s.,]/g, '') || '$';
    return { symbol, before: sample.indexOf(symbol) <= sample.indexOf('0') };
  }, []);

  const [view, setView] = useState('calculator');
  const [draft] = useState(readDraft);
  const [inputs, setInputs] = useState(() => (draft ? draft.inputs : blankInputs()));
  const [years, setYears] = useState(() => (draft ? draft.years : 10));
  const [showInfo, setShowInfo] = useState(false);
  // Id of the snapshot the calculator was opened from, if any.
  const [openedFrom, setOpenedFrom] = useState(() => (draft ? draft.openedFrom : null));
  const [sheet, setSheet] = useState(null); // { mode: 'save' } | { mode: 'edit', id }
  const [armedDelete, setArmedDelete] = useState(null);
  const [limitHit, setLimitHit] = useState(false);

  const snapshots = useMemo(
    () => (futureWorth && Array.isArray(futureWorth.snapshots) ? futureWorth.snapshots : []),
    [futureWorth]
  );
  const [selected, setSelected] = useState(() => snapshots.slice(0, MAX_COMPARE).map((s) => s.id));

  useEffect(() => {
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify({ inputs, years, openedFrom }));
    } catch (e) {
      // Storage full or unavailable - the draft just won't survive leaving.
    }
  }, [inputs, years, openedFrom]);

  const persistSnapshots = (next) => {
    saveUserData({ future_worth: { ...(futureWorth || {}), snapshots: next } });
  };

  const hasData = hasAnyData(inputs);
  const p = useMemo(() => projectFutureWorth(inputs, years), [inputs, years]);

  // ---- editing helpers ----
  const updateEntry = (kind, id, field, value) => {
    setInputs((prev) => ({
      ...prev,
      [kind]: prev[kind].map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    }));
  };
  const addEntry = (kind) => {
    setInputs((prev) => ({
      ...prev,
      [kind]: prev[kind].concat([kind === 'debts' ? blankDebt() : kind === 'assets' ? blankAsset() : blankAccount(kind === 'savings' ? 'sv' : 'iv')]),
    }));
  };
  const removeEntry = (kind, id) => {
    setInputs((prev) => ({ ...prev, [kind]: prev[kind].filter((e) => e.id !== id) }));
  };

  // ---- chart geometry ----
  const lo = Math.min(0, ...p.net, ...p.put);
  let hi = Math.max(...p.net, ...p.put);
  if (hi === lo) hi = lo + 1;
  const yOf = (v) => CHART_H - ((v - lo) / (hi - lo)) * CHART_H;
  const netD = linePath(p.net, lo, hi, years);
  const zeroY = yOf(0).toFixed(1);

  // ---- snapshots ----
  const colorOf = (id) => {
    const i = snapshots.findIndex((s) => s.id === id);
    return snapColors[(i < 0 ? 0 : i) % snapColors.length];
  };
  const compared = snapshots.filter((s) => selected.includes(s.id));
  const comparedProj = compared.map((s) => projectFutureWorth(s.inputs, s.years));
  const maxYears = Math.max(1, ...compared.map((s) => s.years));
  let cLo = 0;
  let cHi = 1;
  comparedProj.forEach((pr) => pr.net.forEach((v) => { if (v < cLo) cLo = v; if (v > cHi) cHi = v; }));

  const toggleCompare = (id) => {
    if (selected.includes(id)) { setSelected(selected.filter((x) => x !== id)); setLimitHit(false); }
    else if (selected.length < MAX_COMPARE) { setSelected(selected.concat([id])); setLimitHit(false); }
    else setLimitHit(true);
  };

  const saveNew = (name, note, updateId) => {
    if (updateId && snapshots.some((s) => s.id === updateId)) {
      persistSnapshots(snapshots.map((s) => (s.id === updateId
        ? { ...s, name: name || s.name, note, years, updatedAt: new Date().toISOString(), inputs: JSON.parse(JSON.stringify(inputs)) }
        : s)));
      setSheet(null);
      setView('snapshots');
      return;
    }
    const snap = {
      id: newId('fw'),
      name: name || 'Snapshot ' + (snapshots.length + 1),
      note,
      years,
      createdAt: new Date().toISOString(),
      inputs: JSON.parse(JSON.stringify(inputs)),
    };
    persistSnapshots(snapshots.concat([snap]));
    setOpenedFrom(snap.id);
    if (selected.length < MAX_COMPARE) setSelected(selected.concat([snap.id]));
    setSheet(null);
    setView('snapshots');
  };

  const saveEdit = (id, name, note) => {
    persistSnapshots(snapshots.map((s) => (s.id === id ? { ...s, name: name || s.name, note } : s)));
    setSheet(null);
  };

  const removeSnapshot = (id) => {
    persistSnapshots(snapshots.filter((s) => s.id !== id));
    setSelected(selected.filter((x) => x !== id));
    if (openedFrom === id) setOpenedFrom(null);
    setArmedDelete(null);
    setLimitHit(false);
  };

  const openInCalculator = (s) => {
    setInputs(normalizeInputs(JSON.parse(JSON.stringify(s.inputs))));
    setYears(s.years);
    setOpenedFrom(s.id);
    setView('calculator');
    { const layer = document.querySelector('.wy-swipe-layer'); (layer || window).scrollTo({ top: 0, behavior: 'smooth' }); } // w2_21
  };

  const editing = sheet && sheet.mode === 'edit' ? snapshots.find((s) => s.id === sheet.id) : null;
  const openedSnapshot = openedFrom ? snapshots.find((s) => s.id === openedFrom) || null : null;

  const sections = [
    { kind: 'savings', title: 'Savings', noun: 'savings account', hint: 'Cash savings, a HISA or a GIC', emoji: '\ud83d\udc37', chip: plan ? '#DFF2E7' : tiles[2].bg },
    { kind: 'investments', title: 'Investments', noun: 'investment', hint: 'A TFSA, RRSP or any investment account', emoji: '\ud83d\udcc8', chip: plan ? '#ECE6FA' : tiles[3].bg },
    { kind: 'assets', title: 'Assets', noun: 'asset', hint: 'Things you own that count toward your net worth, like a car or a home', emoji: '\ud83c\udfe1', chip: plan ? '#DFE9F9' : tiles[0].bg },
    { kind: 'debts', title: 'Debt', noun: 'debt', hint: 'A credit card, loan or line of credit', emoji: '\ud83d\udcb3', chip: plan ? '#FAE4ED' : tiles[1].bg },
  ];

  const debtFreeValue = !p.hasDebt
    ? 'No debt'
    : p.debtFreeMonth === null ? 'Not in ' + years + ' yrs' : duration(p.debtFreeMonth);

  const card = plan ? "bg-white rounded-[28px]" : "bg-white rounded-2xl shadow-sm";

  return (
    <div className="px-4 pt-3 pb-32">
      {/* Purple: header and tabs sit in the gradient band (wy-ph). */}
      <div className="wy-ph" style={{ '--ph-lift': '0.75rem', '--ph-pad': '0rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-1 pt-3">
        <h1 className="m-0 flex items-center" style={{ ...serif, fontSize: '28px', color: '#1A1A1F', letterSpacing: '-0.6px' }}>
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Back to Home"
              className={plan ? 'rounded-full flex items-center justify-center flex-shrink-0' : 'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'}
              style={plan ? { width: 36, height: 36, background: '#FFFFFF', marginRight: '10px' } : { background: track, marginRight: '10px' }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: plan ? PLAN_FW.ink : accent }} />
            </button>
          )}
          {plan ? 'future worth' : 'Future Worth'}
          <button
            onClick={() => setShowInfo(true)}
            aria-label="About Future Worth"
            style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0, marginLeft: '8px', fontFamily: "'DM Sans', sans-serif" }}
          >i</button>
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mt-4 mb-5 wy-tabs" style={{ gap: '48px' }}>
        {[
          { k: 'calculator', label: 'Calculator' },
          { k: 'snapshots', label: 'Snapshots' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setView(t.k)}
            data-on={view === t.k ? '1' : undefined}
            className="relative pb-2 text-[15px]"
            style={{ color: view === t.k ? '#1F2937' : muted, fontWeight: view === t.k ? 700 : 500 }}
          >
            {t.label}
            {view === t.k && (
              <span
                className="absolute left-1/2 bottom-0 h-[3px] rounded-full"
                style={{ width: '34px', transform: 'translateX(-50%)', background: accent }}
              />
            )}
          </button>
        ))}
      </div>
      </div>

      {view === 'calculator' && (
        <>
          {openedSnapshot && (
            <p className="m-0 mb-2 px-1 text-[12.5px] text-gray-500 truncate">
              Opened from <span style={{ color: '#1F2937', fontWeight: 600 }}>{openedSnapshot.name}</span>
            </p>
          )}
          {plan && <PlanFwHero p={p} years={years} hasData={hasData} debtFreeValue={debtFreeValue} />}
          {plan && <div style={{ height: 12 }} />}
          {/* Hero */}
          {!plan && (<>
          <div className="rounded-[22px] wy-tile" style={{ background: tiles[0].bg, padding: '18px 18px 16px' }}>
            <span style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tiles[0].label }}>
              Net worth in {years} {years === 1 ? 'year' : 'years'}
            </span>
            <p className="m-0 mt-1" style={{ ...serif, fontSize: '40px', lineHeight: 1.05, color: tiles[0].value }}>
              {money(hasData ? p.end : 0)}
            </p>
            <p className="m-0 mt-1.5 text-[13px]" style={{ color: tiles[0].label, opacity: 0.85 }}>
              {!hasData
                ? 'Fill in any section below to see where you would be.'
                : p.assetsHeld > 0 ? (p.hasDebt ? 'Your savings, investments and assets, minus any debt left' : 'Your savings, investments and assets combined')
                : p.hasDebt ? 'Your savings and investments, minus any debt left' : 'Your savings and investments combined'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-2 mb-6">
            <Tile emoji={'\ud83c\udfe6'} label="You put in" value={money(hasData ? p.putIn : 0)} tone={tiles[2]} />
            <Tile emoji={'\ud83d\udcc8'} label="Growth" value={money(hasData ? p.growth : 0)} tone={tiles[3]} />
            <Tile emoji={'\ud83d\udcb3'} label="Debt left" value={money(p.debtLeft)} tone={tiles[1]} />
            <Tile emoji={'\ud83d\uddd3\ufe0f'} label="Debt-free in" value={debtFreeValue} tone={plan ? tiles[0] : AMBER_TILE} />
          </div>
          </>)}

          {/* Projection */}
          {!plan && <Eyebrow color={eyebrow}>Projection</Eyebrow>}
          <div className={card + (plan ? " mb-3" : " p-4 mb-6")} style={plan ? { padding: '18px 18px 16px' } : undefined}>
            {plan && <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: PLAN_FW.ink }}>Your projection {'\u{1F4C8}'}</h3>}
            <div className="flex gap-4 text-[12px] text-gray-500">
              <span className="flex items-center gap-1.5">
                <span style={{ width: '14px', height: '3px', borderRadius: '2px', background: netColor }} />
                Net worth
              </span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: '14px', borderTop: '2px dashed ' + putColor }} />
                What you put in
              </span>
            </div>
            {hasData ? (
              <>
                <svg viewBox={'0 -10 ' + CHART_W + ' ' + (CHART_H + 20)} width="100%" height={CHART_H + 20} className="block mt-3" style={{ overflow: 'visible' }}>
                  {plan && (
                    <defs>
                      <linearGradient id="wyPlanFwFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#B9A6F0" stopOpacity="0.55" />
                        <stop offset="1" stopColor="#B9A6F0" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  )}
                  <line x1="0" x2={CHART_W} y1={zeroY} y2={zeroY} stroke={plan ? LINE : '#EADFE6'} strokeWidth="1" />
                  <path d={netD + ' L ' + CHART_W + ' ' + zeroY + ' L 0 ' + zeroY + ' Z'} fill={plan ? 'url(#wyPlanFwFill)' : netColor} fillOpacity={plan ? 1 : 0.1} />
                  <path d={linePath(p.put, lo, hi, years)} fill="none" stroke={putColor} strokeWidth="2" strokeDasharray="5 5" strokeLinecap="round" />
                  <path d={netD} fill="none" stroke={netColor} strokeWidth={plan ? 3 : 2.5} strokeLinejoin="round" strokeLinecap="round" />
                  <circle cx={CHART_W} cy={yOf(p.net[p.net.length - 1]).toFixed(1)} r="4.5" fill={netColor} stroke="#fff" strokeWidth="2" />
                </svg>
                <div className="flex justify-between text-[11.5px] text-gray-500 mt-1">
                  <span>Now</span><span>Yr {Math.round(years / 2)}</span><span>Yr {years}</span>
                </div>
              </>
            ) : (
              <div className="mt-3 rounded-xl flex items-center justify-center text-center text-[13px] text-gray-500 px-6" style={{ height: '150px', background: plan ? PLAN_FW.field : '#FBF5F8', borderRadius: plan ? 18 : undefined }}>
                Your projection will draw here once you add an amount below.
              </div>
            )}
          </div>

          {/* Timeframe */}
          {plan ? (
            <div className={card} style={{ padding: '16px 18px 18px', marginBottom: 26 }} data-testid="plan-fw-years">
              <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: PLAN_FW.ink }}>How long {'\u23F3'}</h3>
                <div className="flex items-center" style={{ gap: 10 }}>
                  <button type="button" aria-label="One year less" onClick={() => setYears(Math.max(1, years - 1))} className="rounded-full flex items-center justify-center" style={{ width: 34, height: 34, background: PLAN_FW.field }}>
                    <Minus className="w-4 h-4" style={{ color: '#6C6879' }} />
                  </button>
                  <span style={{ minWidth: 52, textAlign: 'center', fontSize: 16, fontWeight: 600, color: PLAN_FW.ink }}>{years} {years === 1 ? 'yr' : 'yrs'}</span>
                  <button type="button" aria-label="One year more" onClick={() => setYears(Math.min(50, years + 1))} className="rounded-full flex items-center justify-center" style={{ width: 34, height: 34, background: PLAN_FW.field }}>
                    <Plus className="w-4 h-4" style={{ color: '#6C6879' }} />
                  </button>
                </div>
              </div>
              <div className="flex" style={{ background: '#ECE8F4', borderRadius: 999, padding: 3 }}>
                {YEAR_PRESETS.map((y) => (
                  <button key={y} type="button" onClick={() => setYears(y)}
                    style={{ flex: 1, border: 0, borderRadius: 999, height: 34, fontSize: 13, fontWeight: y === years ? 600 : 500, color: y === years ? PLAN_FW.ink : '#6C6879', background: y === years ? '#FFFFFF' : 'transparent' }}>
                    {y} yrs
                  </button>
                ))}
              </div>
            </div>
          ) : (<>
          <Eyebrow color={eyebrow}>Timeframe</Eyebrow>
          <div className={card + " px-4 py-3 mb-6"}>
            <div className="flex items-center justify-between">
              <span className="text-[15px]" style={{ color: '#1F2937', fontWeight: 500 }}>How long</span>
              <div className="flex items-center gap-1.5">
                <button type="button" aria-label="One year less" onClick={() => setYears(Math.max(1, years - 1))} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#F8F1F5' }}>
                  <Minus className="w-4 h-4" style={{ color: accent }} />
                </button>
                <span className="text-center text-[16px] font-bold" style={{ minWidth: '64px', color: '#1F2937' }}>
                  {years} {years === 1 ? 'yr' : 'yrs'}
                </span>
                <button type="button" aria-label="One year more" onClick={() => setYears(Math.min(50, years + 1))} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#F8F1F5' }}>
                  <Plus className="w-4 h-4" style={{ color: accent }} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {YEAR_PRESETS.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setYears(y)}
                  className="rounded-full text-[13px] font-semibold"
                  style={{ height: '38px', background: y === years ? accent : '#F8F1F5', color: y === years ? '#fff' : '#6B4F5E' }}
                >
                  {y} yrs
                </button>
              ))}
            </div>
          </div>
          </>)}

          {/* Savings / Investments / Debt */}
          {sections.map((sec) => {
            const list = inputs[sec.kind];
            const total = list.reduce((t, e) => t + num(sec.kind === 'debts' ? e.balance : sec.kind === 'assets' ? e.value : e.start), 0);
            const capital = sec.noun.charAt(0).toUpperCase() + sec.noun.slice(1);
            return (
              <div key={sec.kind} className="mb-6">
                <Eyebrow
                  color={eyebrow}
                  right={list.length > 1 ? list.length + ' \u00b7 ' + money(total) + (sec.kind === 'debts' ? ' owed' : ' today') : null}
                >
                  {sec.title}
                </Eyebrow>
                <div className="flex flex-col gap-2.5">
                  {list.map((entry, i) => (
                    <EntryCard
                      key={entry.id}
                      kind={sec.kind}
                      entry={entry}
                      fallbackName={capital + ' ' + (i + 1)}
                      emoji={sec.emoji}
                      chip={sec.chip}
                      currency={currency}
                      accent={accent}
                      warn={p.growingDebtIds.includes(entry.id)}
                      onChange={(field, value) => updateEntry(sec.kind, entry.id, field, value)}
                      onRemove={() => removeEntry(sec.kind, entry.id)}
                    />
                  ))}
                  {list.length === 0 && (
                    <p className="m-0 px-1 text-[13px] text-gray-500">{sec.hint}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => addEntry(sec.kind)}
                    className="rounded-full flex items-center justify-center gap-1.5 text-[15px] font-semibold"
                    style={plan ? { height: '48px', border: '2px dashed #DCD3F4', color: '#7A5DD3', fontSize: 14.5 } : { height: '48px', border: '1.5px dashed #DCC7D3', color: accent }}
                  >
                    <Plus className="w-4 h-4" />
                    {list.length ? 'Add another ' + sec.noun : 'Add ' + sec.noun}
                  </button>
                </div>
              </div>
            );
          })}

          <div className="flex gap-2.5 mt-2">
            <button
              type="button"
              onClick={() => { setInputs(blankInputs()); setYears(10); setOpenedFrom(null); }}
              className="rounded-full text-[15px] font-semibold px-6"
              style={plan ? { height: '52px', background: '#FFFFFF', color: '#55565C' } : { height: '52px', border: '1.5px solid #E6D3DD', color: '#6B4F5E' }}
            >
              Clear
            </button>
            <button
              type="button"
              disabled={!hasData}
              onClick={() => setSheet({ mode: 'save' })}
              className="flex-1 rounded-full text-[15px] font-bold text-white"
              style={{ height: '52px', background: accent, opacity: hasData ? 1 : 0.45 }}
            >
              Save snapshot
            </button>
          </div>

          <p className="m-0 mt-5 px-1 text-[12.5px] leading-relaxed text-gray-500">
            {plan ? 'Projections assume the same rate every year. Real returns rise and fall, so this is an estimate to help you plan, not financial advice.' : 'Projections assume the same rate every year and contributions made monthly. Real returns rise and fall. This is an estimate to help you plan, not financial advice.'}
          </p>
        </>
      )}

      {view === 'snapshots' && snapshots.length === 0 && (
        <div className={card + " px-6 py-7 text-center"}>
          <div className="mx-auto rounded-full flex items-center justify-center" style={{ width: plan ? '50px' : '48px', height: plan ? '50px' : '48px', background: plan ? '#ECE6FA' : track, fontSize: '22px' }}>
            {'\ud83d\uddc2\ufe0f'}
          </div>
          <p className="m-0 mt-3 text-[17px] font-semibold" style={{ color: '#1F2937' }}>No snapshots yet</p>
          <p className="m-0 mt-1.5 text-[14px] text-gray-500 leading-relaxed">
            Fill in the calculator, then save a snapshot to compare plans side by side.
          </p>
          <button
            type="button"
            onClick={() => setView('calculator')}
            className={plan ? 'mt-4 rounded-full text-[15px] font-semibold text-white px-6' : 'mt-4 rounded-full text-[15px] font-bold text-white px-6'}
            style={{ height: '48px', background: accent }}
          >
            Go to calculator
          </button>
        </div>
      )}

      {view === 'snapshots' && snapshots.length > 0 && (
        <>
          <Eyebrow color={eyebrow}>{plan ? 'Compare \u{1F4CA}' : 'Compare'}</Eyebrow>
          <div className={card + " p-4 mb-6"} style={plan ? { padding: '18px 18px 14px' } : undefined}>
            {compared.length > 0 ? (
              <>
                <svg viewBox={'0 -10 ' + CHART_W + ' ' + (CHART_H + 20)} width="100%" height={CHART_H + 20} className="block" style={{ overflow: 'visible' }}>
                  <line x1="0" x2={CHART_W} y1={(CHART_H - ((0 - cLo) / (cHi - cLo || 1)) * CHART_H).toFixed(1)} y2={(CHART_H - ((0 - cLo) / (cHi - cLo || 1)) * CHART_H).toFixed(1)} stroke={plan ? LINE : '#EADFE6'} strokeWidth="1" />
                  {compared.map((s, i) => (
                    <path key={s.id} d={linePath(comparedProj[i].net, cLo, cHi, maxYears)} fill="none" stroke={colorOf(s.id)} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                  ))}
                </svg>
                <div className="flex justify-between text-[11.5px] text-gray-500 mt-1">
                  <span>Now</span><span>Yr {maxYears}</span>
                </div>
                <div className="mt-3" style={{ borderTop: '1px solid ' + LINE }}>
                  {compared.map((s, i) => {
                    const diff = comparedProj[i].end - comparedProj[0].end;
                    return (
                      <div key={s.id} className="flex items-center gap-2.5 py-2.5" style={{ borderBottom: '1px solid ' + LINE }}>
                        <span className="rounded-full flex-shrink-0" style={{ width: '10px', height: '10px', background: colorOf(s.id) }} />
                        <div className="flex-1 min-w-0">
                          <p className="m-0 text-[15px] truncate" style={{ color: '#1F2937', fontWeight: 500 }}>{s.name}</p>
                          <p className="m-0 text-[12px]" style={{ color: i === 0 ? muted : diff >= 0 ? POSITIVE : NEGATIVE }}>
                            {i === 0 ? 'Baseline' : (diff >= 0 ? '+' : '') + money(diff) + ' vs ' + compared[0].name}
                          </p>
                        </div>
                        <span style={{ ...serif, ...bigNum, fontSize: plan ? '17px' : '19px', color: plan ? PLAN_FW.ink : colorOf(s.id) }}>{money(comparedProj[i].end)}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="m-0 py-10 text-center text-[14px] text-gray-500">
                Tick up to three snapshots below to see them on one chart.
              </p>
            )}
          </div>

          <Eyebrow
            color={eyebrow}
            right={limitHit ? 'Untick one to add another' : compared.length + ' of ' + MAX_COMPARE + ' compared'}
          >
            Saved
          </Eyebrow>
          <div className="flex flex-col gap-3">
            {snapshots.map((s) => {
              const on = selected.includes(s.id);
              const c = colorOf(s.id);
              const pr = projectFutureWorth(s.inputs, s.years);
              const inp = normalizeInputs(s.inputs);
              const accts = inp.savings.concat(inp.investments);
              const startT = accts.reduce((t, a) => t + num(a.start), 0);
              const monthlyT = accts.reduce((t, a) => t + monthlyEquivalent(num(a.monthly), a.freq), 0);
              const owedT = inp.debts.reduce((t, d) => t + num(d.balance), 0);
              const heldT = inp.assets.reduce((t, a) => t + num(a.value), 0);
              const summary = [
                startT ? money(startT) + ' to start' : null,
                monthlyT ? money(monthlyT) + '/mo' : null,
                heldT ? money(heldT) + ' in assets' : null,
                owedT ? money(owedT) + ' debt' : null,
              ].filter(Boolean).join(', ');
              const armed = armedDelete === s.id;

              return (
                <div key={s.id} className={card} style={{ padding: plan ? '16px 12px 14px 18px' : '14px 10px 12px 16px' }}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 pt-1">
                      <p className="m-0 flex items-center gap-2 text-[15px]" style={{ color: '#1F2937', fontWeight: 500 }}>
                        <span className="rounded-full flex-shrink-0" style={{ width: '10px', height: '10px', background: c }} />
                        <span className="truncate" style={plan ? { fontWeight: 600, color: PLAN_FW.ink } : undefined}>{s.name}</span>
                      </p>
                      {summary && <p className="m-0 mt-0.5 text-[12.5px] text-gray-500">{summary}</p>}
                    </div>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      aria-label={'Compare ' + s.name}
                      onClick={() => toggleCompare(s.id)}
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                    >
                      <span
                        className="rounded-full flex items-center justify-center"
                        style={{ width: plan ? '26px' : '24px', height: plan ? '26px' : '24px', background: on ? c : '#fff', border: on ? 'none' : '2px solid ' + (plan ? '#DCD3F4' : '#D9CBD3') }}
                      >
                        {on && (
                          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                            <path d="M3 7.2l2.6 2.6L11 4.4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                    </button>
                  </div>

                  <div className="flex items-baseline gap-2 mt-1.5">
                    <span style={{ ...serif, ...bigNum, fontSize: '26px', color: plan ? PLAN_FW.ink : c }}>{money(pr.end)}</span>
                    <span className="text-[12.5px] text-gray-500">in {s.years} {s.years === 1 ? 'year' : 'years'}</span>
                  </div>

                  {s.note ? (
                    <p className="m-0 mt-2 mr-1.5 rounded-xl text-[14px] leading-snug whitespace-pre-line" style={{ background: plan ? PLAN_FW.field : '#FBF5F8', color: '#4B4550', padding: '9px 12px', borderRadius: plan ? 16 : undefined }}>
                      {s.note}
                    </p>
                  ) : null}

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setSheet({ mode: 'edit', id: s.id })}
                      className="rounded-full text-[13.5px] font-semibold px-4"
                      style={plan ? { height: '38px', background: PLAN_FW.field, color: '#55565C' } : { height: '38px', border: '1.5px solid #EBDCE4', color: '#6B4F5E' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => openInCalculator(s)}
                      className="rounded-full text-[13.5px] font-semibold px-4"
                      style={plan ? { height: '38px', background: '#1F1E24', color: '#FFFFFF' } : { height: '38px', background: '#F8F1F5', color: '#6B4F5E' }}
                    >
                      Open in calculator
                    </button>
                    <span className="flex-1" />
                    {armed ? (
                      <button
                        type="button"
                        onClick={() => removeSnapshot(s.id)}
                        className="rounded-full text-[13px] font-bold px-3.5"
                        style={plan ? { height: '38px', background: '#FAE4ED', color: '#C04B7C', fontWeight: 600 } : { height: '38px', background: WARN.bg, color: WARN.text }}
                      >
                        Delete
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setArmedDelete(s.id)}
                        aria-label={'Delete ' + s.name}
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                      >
                        <Trash2 className="w-[17px] h-[17px]" style={{ color: plan ? '#C4BADF' : '#A39FA0' }} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showInfo && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowInfo(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl overflow-y-auto overscroll-contain" style={{ maxHeight: '88vh', touchAction: 'pan-y' }}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>About Future Worth</h3>
            <div className="space-y-4">
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\ud83d\udd2e'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">What this is for</p><p className="text-xs text-gray-500 leading-relaxed">See where a plan could take you. Nothing here is connected to your Worthly accounts, so you can try out any idea.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\u270f\ufe0f'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">How to use it</p><p className="text-xs text-gray-500 leading-relaxed">Add your savings, investments, assets and debts, each as its own card, then choose how many years to look ahead. Your future net worth at the top of the page updates as you type.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\ud83d\udcc8'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Savings and investments</p><p className="text-xs text-gray-500 leading-relaxed">Enter what&apos;s in the account today, how much you&apos;ll add and how often, and the yearly interest or growth rate. Growth is added monthly at the same rate every year. Real investments rise and fall, so treat this as a guide, not a promise.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\ud83c\udfe1'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Assets</p><p className="text-xs text-gray-500 leading-relaxed">Add things you own that count toward your net worth, like a car or a home, with what each is worth today. It counts now and in every year after. Choose whether its value stays the same, goes up or goes down each year, like a home gaining value or a car losing it, and enter by how much. Either way it isn&apos;t counted as money you put in or as growth.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\ud83d\udcb3'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Debt</p><p className="text-xs text-gray-500 leading-relaxed">Enter what you owe right now, as shown on your latest statement. Don&apos;t include interest you haven&apos;t been charged yet, because Future Worth adds interest each month from the rate you enter. Then add how much you&apos;ll pay and how often. If your payments don&apos;t cover the interest, the card will warn you that the balance is growing.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\ud83d\uddc2\ufe0f'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Snapshots</p><p className="text-xs text-gray-500 leading-relaxed">Save a plan to come back to it, give it a name and a note, and compare up to three on one chart. Snapshots keep the numbers you entered, so open one in the calculator to try changes and save the result as a new snapshot.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">{'\u26a0\ufe0f'}</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">An estimate, not advice</p><p className="text-xs text-gray-500 leading-relaxed">This is an estimate to help you plan, not financial advice.</p></div></div>
            </div>
            <button onClick={() => setShowInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-sm">Got it</button>
          </div>
        </div>,
        document.body
      )}

      {sheet && sheet.mode === 'save' && (
        <SnapshotSheet
          mode="save"
          placeholder={'Snapshot ' + (snapshots.length + 1)}
          years={years}
          value={money(p.end)}
          hero={tiles[0]}
          accent={accent}
          track={track}
          source={openedSnapshot}
          onSave={saveNew}
          onClose={() => setSheet(null)}
        />
      )}

      {editing && (
        <SnapshotSheet
          key={editing.id}
          mode="edit"
          initialName={editing.name}
          initialNote={editing.note}
          placeholder={editing.name}
          years={editing.years}
          value={money(projectFutureWorth(editing.inputs, editing.years).end)}
          hero={tiles[0]}
          accent={accent}
          track={track}
          onSave={(name, note) => saveEdit(editing.id, name, note)}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
};

export default FutureWorthTab;
